/**
 * Read the Electron main-process clipboard bridge if it's exposed
 * (`apps/desktop-electron/src/preload/index.ts`). Absent on web.
 */
function getElectronClipboardWrite():
  | ((text: string) => Promise<boolean>)
  | null {
  const api = (
    window as unknown as {
      __electronApi?: {
        writeClipboardText?: (text: string) => Promise<boolean>;
      };
    }
  ).__electronApi;
  return api?.writeClipboardText ?? null;
}

/**
 * Copy `text` to the clipboard with a fallback that works when
 * `navigator.clipboard.writeText` rejects (insecure context, document
 * focus moved off, restrictive permission policy, etc.).
 *
 * In Electron the renderer runs under the custom `ano-app://localhost`
 * scheme, where `navigator.clipboard.writeText` can resolve WITHOUT
 * actually writing, and the `execCommand("copy")` fallback fails inside
 * Radix dialog focus traps (the invite "copy link" surface). So when the
 * main-process clipboard bridge is present we use it first — it's
 * independent of document focus and secure-context state.
 *
 * The web fallback drops a hidden `<textarea>`, selects it, and dispatches
 * `document.execCommand("copy")` — deprecated but universally supported,
 * doesn't require Clipboard API permissions, and works in every browser
 * build we ship. Mirrors the legacy pattern previously inlined in
 * `CardActions.tsx`; consolidated here so every "copy this" surface has
 * the same resilience.
 *
 * Rejects only if ALL available paths fail.
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (!text) return;

  const electronWrite = getElectronClipboardWrite();
  if (electronWrite) {
    try {
      if (await electronWrite(text)) return;
      console.warn(
        "[clipboard] electron bridge returned false, falling back to web path",
      );
    } catch (err) {
      console.warn(
        "[clipboard] electron clipboard bridge threw, falling back to web path",
        err,
      );
    }
  }

  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch (err) {
    // Log so the next misfire is diagnosable; the user-facing toast
    // happens at the call site.
    console.warn(
      "[clipboard] navigator.clipboard.writeText failed, falling back to execCommand",
      err,
    );
  }

  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.opacity = "0";
  ta.setAttribute("readonly", "");
  ta.setAttribute("aria-hidden", "true");
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try {
    const ok = document.execCommand("copy");
    if (!ok) throw new Error("execCommand('copy') returned false");
  } finally {
    document.body.removeChild(ta);
  }
}
